<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import {
    convertDocumentToMarkdown,
    formatDocumentName,
    extractDocumentMetadata,
    checkPandocHealth,
    type DocumentMetadata,
    type WrapTextMode
  } from '../../services/pandocService';
  import type { CreateImageInput, CreateTemplateInput, TemplateType, TemplateSection, DocumentSection, DocumentType } from '../../types';
  import JSZip from 'jszip';

  // Types
  interface ConversionProgressState {
    stage: string;
    progress: number;
    current: number;
    total: number;
    currentFile?: string;
  }

  interface ServerStatus {
    status: string;
    message?: string;
    pandocVersion?: string;
  }

  interface ConversionOptions {
    extractMedia: boolean;
    preserveHeadings: boolean;
    wrapText: WrapTextMode;
  }

  interface ConvertedFile {
    markdown: string;
    filename: string;
    originalFilename: string;
    metadata: DocumentMetadata;
    size: number;
  }

  interface ConvertedEventDetail {
    files: ConvertedFile[];
    count: number;
  }

  // File categories
  type FileCategory = 'image' | 'template' | 'document' | 'text' | 'zip';

  interface CategorizedFile {
    file: File;
    category: FileCategory;
    icon: string;
  }

  /** Metadata extracted from a ZIP manifest */
  interface ZipMetadataEvent {
    metadata: string;
    title?: string;
    author?: string;
  }

  const dispatch = createEventDispatcher<{
    converted: ConvertedEventDetail;
    imagesImported: { images: CreateImageInput[]; count: number };
    templatesImported: { templates: CreateTemplateInput[]; count: number };
    metadataImported: ZipMetadataEvent;
  }>();

  // États
  let isDragging: boolean = false;
  let isProcessing: boolean = false;
  let conversionProgress: ConversionProgressState = { stage: '', progress: 0, current: 0, total: 0 };
  let error: string | null = null;
  let warnings: string[] = [];
  let serverStatus: ServerStatus | null = null;
  let selectedFiles: CategorizedFile[] = [];

  // Options de conversion
  let options: ConversionOptions = {
    extractMedia: true,
    preserveHeadings: true,
    wrapText: 'auto'
  };

  // Configuration
  const MAX_FILES: number = 50;
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TEMPLATE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_DOC_SIZE = 20 * 1024 * 1024; // 20MB
  const MAX_ZIP_SIZE = 200 * 1024 * 1024; // 200MB

  // Vérifier le serveur au chargement
  checkServerStatus();

  async function checkServerStatus(): Promise<void> {
    try {
      serverStatus = await checkPandocHealth();
    } catch (err) {
      serverStatus = { status: 'error', message: (err as Error).message };
    }
  }

  /**
   * Categorize file based on extension and MIME type
   */
  function categorizeFile(file: File): { category: FileCategory; icon: string } {
    const ext = file.name.toLowerCase().split('.').pop() || '';
    const mimeType = file.type.toLowerCase();

    console.log('🔍 Categorizing file:', { filename: file.name, ext, mimeType });

    // ZIP archives (AutomaticBook project export)
    if (ext === 'zip' || mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') {
      console.log('  → zip');
      return { category: 'zip', icon: '📦' };
    }

    // Images
    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
      console.log('  → image');
      return { category: 'image', icon: '🖼️' };
    }

    // Templates (CSS, fonts, Lua filters, CSL)
    if (ext === 'css' || mimeType === 'text/css') {
      console.log('  → template (css)');
      return { category: 'template', icon: '🎨' };
    }
    if (['ttf', 'otf', 'woff', 'woff2'].includes(ext) || mimeType.includes('font')) {
      console.log('  → template (font)');
      return { category: 'template', icon: '🔤' };
    }
    if (ext === 'lua') {
      console.log('  → template (lua)');
      return { category: 'template', icon: '🌙' };
    }
    if (ext === 'csl') {
      console.log('  → template (csl)');
      return { category: 'template', icon: '📚' };
    }
    if (ext === 'indd') {
      console.log('  → template (indesign)');
      return { category: 'template', icon: '📐' };
    }

    // Plain text files (direct import, no conversion needed)
    if (['md', 'markdown', 'txt'].includes(ext) || mimeType === 'text/plain' || mimeType === 'text/markdown') {
      console.log('  → text');
      return { category: 'text', icon: '📝' };
    }

    // Documents needing conversion
    console.log('  → document (fallback)');
    return { category: 'document', icon: '📄' };
  }

  /**
   * Validate file based on its category
   */
  function validateFile(file: File, category: FileCategory): { valid: boolean; error?: string } {
    switch (category) {
      case 'image':
        if (file.size > MAX_IMAGE_SIZE) {
          return { valid: false, error: `Image too large (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB)` };
        }
        break;
      case 'template':
        if (file.size > MAX_TEMPLATE_SIZE) {
          return { valid: false, error: `Template too large (max ${MAX_TEMPLATE_SIZE / 1024 / 1024}MB)` };
        }
        break;
      case 'document':
      case 'text':
        if (file.size > MAX_DOC_SIZE) {
          return { valid: false, error: `Document too large (max ${MAX_DOC_SIZE / 1024 / 1024}MB)` };
        }
        break;
      case 'zip':
        if (file.size > MAX_ZIP_SIZE) {
          return { valid: false, error: `ZIP too large (max ${MAX_ZIP_SIZE / 1024 / 1024}MB)` };
        }
        break;
    }
    return { valid: true };
  }

  function handleDragOver(event: DragEvent): void {
    event.preventDefault();
    isDragging = true;
  }

  function handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    isDragging = false;
  }

  function handleDrop(event: DragEvent): void {
    event.preventDefault();
    isDragging = false;

    const files = Array.from(event.dataTransfer?.files || []);
    handleFilesSelect(files);
  }

  function handleFileInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files || []);
    handleFilesSelect(files);
    // Reset input to allow selecting same files again
    target.value = '';
  }

  function handleFilesSelect(files: File[]): void {
    error = null;
    warnings = [];

    // Check total count
    const newTotal = selectedFiles.length + files.length;
    if (newTotal > MAX_FILES) {
      error = `Maximum ${MAX_FILES} files. You selected ${files.length} files but ${selectedFiles.length} are already in the list.`;
      return;
    }

    // Validate and categorize each file
    const validFiles: CategorizedFile[] = [];
    const fileWarnings: string[] = [];

    for (const file of files) {
      // Check for duplicates
      if (selectedFiles.some(f => f.file.name === file.name && f.file.size === file.size)) {
        fileWarnings.push(`"${file.name}" is already in the list`);
        continue;
      }

      const { category, icon } = categorizeFile(file);
      const validation = validateFile(file, category);

      if (!validation.valid) {
        fileWarnings.push(`${file.name}: ${validation.error}`);
        continue;
      }

      validFiles.push({ file, category, icon });
    }

    if (fileWarnings.length > 0) {
      warnings = fileWarnings;
    }

    if (validFiles.length > 0) {
      selectedFiles = [...selectedFiles, ...validFiles];
    }
  }

  function removeFile(index: number): void {
    selectedFiles = selectedFiles.filter((_, i) => i !== index);
    if (selectedFiles.length === 0) {
      warnings = [];
    }
  }

  // Helper functions for file reading
  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
  }

  function getTemplateType(filename: string, mimeType: string): TemplateType {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'css' || mimeType === 'text/css') {
      return 'css';
    }
    if (['ttf', 'otf', 'woff', 'woff2'].includes(ext || '') || mimeType.includes('font')) {
      return 'font';
    }
    if (ext === 'lua') {
      return 'lua';
    }
    if (ext === 'csl') {
      return 'csl';
    }
    if (ext === 'indd') {
      return 'indesign';
    }
    return 'other';
  }

  /**
   * Detect MIME type from filename extension
   */
  function mimeFromExt(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const mimeMap: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon',
      css: 'text/css', lua: 'text/x-lua', csl: 'application/xml',
      ttf: 'font/ttf', otf: 'font/otf', woff: 'font/woff', woff2: 'font/woff2',
      indd: 'application/x-indesign', json: 'application/json',
      md: 'text/markdown', yaml: 'text/yaml', yml: 'text/yaml'
    };
    return mimeMap[ext] || 'application/octet-stream';
  }

  /**
   * Parse a simple YAML manifest (flat key/value + texts array).
   * This is a lightweight parser — only extracts what we need from the manifest.
   */
  function parseSimpleYaml(yaml: string): any {
    const result: any = { texts: [], assets: {}, config: {}, templates: {} };
    const lines = yaml.split('\n');
    let currentArray: any[] | null = null;
    let currentArrayKey = '';
    let currentObj: any = null;
    let inTemplatesSection = false;
    let currentTemplateGroup = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.replace(/#.*$/, '').trimEnd();
      if (!trimmed) continue;

      // Top-level array item (texts)
      const arrayItemMatch = trimmed.match(/^  - file:\s*(.+)/);
      if (arrayItemMatch && currentArrayKey === 'texts') {
        currentObj = { file: arrayItemMatch[1].trim().replace(/^['"]|['"]$/g, '') };
        currentArray!.push(currentObj);
        continue;
      }

      // Template section array item
      const tplItemMatch = trimmed.match(/^      - file:\s*(.+)/);
      if (tplItemMatch && inTemplatesSection && currentTemplateGroup) {
        currentObj = { file: tplItemMatch[1].trim().replace(/^['"]|['"]$/g, '') };
        if (!result.templates[currentTemplateGroup]) result.templates[currentTemplateGroup] = [];
        result.templates[currentTemplateGroup].push(currentObj);
        continue;
      }
      // Also handle 4-space indent for template items
      const tplItemMatch2 = trimmed.match(/^    - file:\s*(.+)/);
      if (tplItemMatch2 && inTemplatesSection && currentTemplateGroup) {
        currentObj = { file: tplItemMatch2[1].trim().replace(/^['"]|['"]$/g, '') };
        if (!result.templates[currentTemplateGroup]) result.templates[currentTemplateGroup] = [];
        result.templates[currentTemplateGroup].push(currentObj);
        continue;
      }

      // Property of current array item (type, lastModified)
      const propMatch = trimmed.match(/^    (\w+):\s*(.+)/);
      if (propMatch && currentObj) {
        currentObj[propMatch[1]] = propMatch[2].trim().replace(/^['"]|['"]$/g, '');
        continue;
      }

      // Template sub-property (6-space)
      const tplPropMatch = trimmed.match(/^      (\w+):\s*(.+)/);
      if (tplPropMatch && currentObj && inTemplatesSection) {
        currentObj[tplPropMatch[1]] = tplPropMatch[2].trim().replace(/^['"]|['"]$/g, '');
        continue;
      }

      // Template group header (e.g. "  indesign:" under templates:)
      const tplGroupMatch = trimmed.match(/^  (\w+):\s*$/);
      if (tplGroupMatch && inTemplatesSection) {
        currentTemplateGroup = tplGroupMatch[1];
        currentObj = null;
        continue;
      }

      // Top-level section headers
      if (trimmed === 'texts:') {
        currentArray = result.texts;
        currentArrayKey = 'texts';
        currentObj = null;
        inTemplatesSection = false;
        continue;
      }
      if (trimmed === 'assets:') {
        currentArray = null;
        currentArrayKey = 'assets';
        currentObj = null;
        inTemplatesSection = false;
        continue;
      }
      if (trimmed === 'config:') {
        currentArray = null;
        currentArrayKey = 'config';
        currentObj = null;
        inTemplatesSection = false;
        continue;
      }
      if (trimmed === 'templates:') {
        currentArray = null;
        currentArrayKey = 'templates';
        currentObj = null;
        inTemplatesSection = true;
        currentTemplateGroup = '';
        continue;
      }

      // Sub-keys of assets/config sections
      const subKeyMatch = trimmed.match(/^  (\w[\w-]*):\s*(.+)/);
      if (subKeyMatch) {
        const val = subKeyMatch[2].trim().replace(/^['"]|['"]$/g, '');
        if (currentArrayKey === 'assets') {
          result.assets[subKeyMatch[1]] = val;
        } else if (currentArrayKey === 'config') {
          result.config[subKeyMatch[1]] = val;
        }
        currentObj = null;
        continue;
      }

      // Top-level key: value
      const kvMatch = trimmed.match(/^(\w[\w-]*):\s*(.+)/);
      if (kvMatch) {
        result[kvMatch[1]] = kvMatch[2].trim().replace(/^['"]|['"]$/g, '');
        currentArray = null;
        currentArrayKey = '';
        currentObj = null;
        inTemplatesSection = false;
        continue;
      }
    }
    return result;
  }

  /**
   * Process an AutomaticBook ZIP export.
   * Reads manifest.yaml, then extracts texts, images, templates, configs, and metadata.
   */
  async function processZipFile(file: File): Promise<void> {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    // 1. Find and parse manifest.yaml
    const manifestEntry = zip.file('manifest.yaml') || zip.file('manifest.yml');
    if (!manifestEntry) {
      throw new Error('Not an AutomaticBook ZIP: manifest.yaml not found');
    }
    const manifestText = await manifestEntry.async('text');
    const manifest = parseSimpleYaml(manifestText);
    console.log('📦 Parsed manifest:', manifest);

    const totalItems = (manifest.texts?.length || 0) + Object.keys(zip.files).length;
    let processed = 0;

    // 2. Import metadata.yaml
    const metadataPath = manifest.metadata || 'metadata.yaml';
    const metadataEntry = zip.file(metadataPath);
    if (metadataEntry) {
      let metaYaml = await metadataEntry.async('text');
      // Strip --- delimiters for storage
      metaYaml = metaYaml.trim();
      if (metaYaml.startsWith('---')) metaYaml = metaYaml.replace(/^---\s*\n?/, '');
      if (metaYaml.endsWith('---')) metaYaml = metaYaml.replace(/\n?---\s*$/, '');
      metaYaml = metaYaml.trim();

      // Extract title and author from YAML for convenience
      let title: string | undefined;
      let author: string | undefined;
      const titleMatch = metaYaml.match(/^title:\s*['"]?(.+?)['"]?\s*$/m);
      if (titleMatch) title = titleMatch[1];
      // Pandoc array format: title:\n- type: main\n  text: "..."
      const titleArrayMatch = metaYaml.match(/^title:\s*\n\s*-\s*type:\s*main\s*\n\s*text:\s*['"]?(.+?)['"]?\s*$/m);
      if (titleArrayMatch) title = titleArrayMatch[1];

      const authorMatch = metaYaml.match(/^(?:author|creator):\s*['"]?(.+?)['"]?\s*$/m);
      if (authorMatch && !authorMatch[1].startsWith('-')) author = authorMatch[1];

      dispatch('metadataImported', { metadata: metaYaml, title, author });
      console.log('📋 Metadata dispatched:', { title, author });
    }

    // 3. Import texts from manifest entries
    const textResults: ConvertedFile[] = [];
    if (manifest.texts?.length) {
      conversionProgress = { stage: 'Extracting documents...', progress: 0, current: 0, total: manifest.texts.length };

      for (const entry of manifest.texts) {
        const filePath = entry.file;
        const zipEntry = zip.file(filePath);
        if (!zipEntry) {
          console.warn(`⚠️ Text file not found in ZIP: ${filePath}`);
          continue;
        }

        const content = await zipEntry.async('text');
        const filename = filePath.split('/').pop() || 'document.md';
        const metadata = extractDocumentMetadata(content);

        // Attach section info from manifest
        (metadata as any)._section = entry.type as DocumentSection || 'bodymatter';

        textResults.push({
          markdown: content,
          filename: formatDocumentName(filename),
          originalFilename: filename,
          metadata,
          size: content.length
        });
        processed++;
        conversionProgress = { ...conversionProgress, current: textResults.length, progress: Math.round((processed / totalItems) * 100) };
      }
    }

    if (textResults.length > 0) {
      dispatch('converted', { files: textResults, count: textResults.length });
    }

    // 4. Import images from assets/
    const images: CreateImageInput[] = [];
    const assetPrefix = 'assets/';
    const coverFilename = manifest.assets?.cover?.split('/').pop();

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir || !path.startsWith(assetPrefix)) continue;
      const filename = path.split('/').pop() || '';
      const ext = filename.toLowerCase().split('.').pop() || '';
      if (!['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) continue;

      const base64 = await entry.async('base64');
      const mime = mimeFromExt(filename);
      const dataUrl = `data:${mime};base64,${base64}`;

      const isCover = coverFilename && filename === coverFilename;

      images.push({
        projectId: '',
        filename,
        mimeType: mime,
        dataUrl,
        size: (await entry.async('uint8array')).length,
        imageSection: isCover ? 'cover' : 'images',
        width: 0,
        height: 0
      });
      processed++;
    }

    if (images.length > 0) {
      dispatch('imagesImported', { images, count: images.length });
    }

    // 5. Import templates
    const tplResults: CreateTemplateInput[] = [];

    // Known document types for preparo template matching
    const DOCUMENT_TYPES: Set<string> = new Set([
      'prologue', 'abstract', 'acknowledgments', 'copyright-page', 'dedication',
      'credits', 'keywords', 'imprint', 'contributors', 'other-credits', 'errata',
      'revision-history', 'titlepage', 'halftitlepage', 'seriespage', 'foreword',
      'preface', 'frontispiece', 'introduction', 'chapter', 'part', 'appendix',
      'colophon', 'bibliography', 'index', 'conclusion', 'annexe', 'postface'
    ]);

    // Helper to add a template from a zip path
    const addTemplate = async (path: string, tplType: TemplateType, tplSection: TemplateSection, docType?: string) => {
      const entry = zip.file(path);
      if (!entry) return;
      const filename = path.split('/').pop() || '';
      const mime = mimeFromExt(filename);
      const isBinary = ['font', 'indesign'].includes(tplType);
      const content = isBinary ? await entry.async('base64') : await entry.async('text');

      // Derive documentType for preparo templates
      let documentType: DocumentType | undefined;
      if (tplType === 'preparo') {
        // 1. From manifest or explicit docType parameter
        if (docType && DOCUMENT_TYPES.has(docType)) {
          documentType = docType as DocumentType;
        }
        // 2. From filename (e.g. titlepage.md → "titlepage")
        if (!documentType) {
          const nameCandidate = filename.replace(/\.md$/i, '');
          if (DOCUMENT_TYPES.has(nameCandidate)) {
            documentType = nameCandidate as DocumentType;
          }
        }
        // 3. From epub:type attribute in content (e.g. {epub:type=titlepage})
        if (!documentType && !isBinary) {
          const epubMatch = content.match(/^#\s+.+\{([^}]+)\}\s*$/m);
          if (epubMatch) {
            const typeMatch = epubMatch[1].match(/epub:type=(\S+)/);
            if (typeMatch) {
              const epubType = typeMatch[1].replace(/^["']|["']$/g, '');
              if (DOCUMENT_TYPES.has(epubType)) {
                documentType = epubType as DocumentType;
              }
            }
          }
        }
      }

      tplResults.push({
        projectId: '',
        filename,
        templateType: tplType,
        templateSection: tplSection,
        mimeType: mime,
        content,
        size: (await entry.async('uint8array')).length,
        documentType: documentType ?? null
      });
    };

    // Templates from manifest.templates section (if present)
    if (manifest.templates) {
      for (const tplEntry of (manifest.templates.indesign || [])) {
        await addTemplate(tplEntry.file, 'indesign', 'indesign');
      }
      for (const tplEntry of (manifest.templates.epub || [])) {
        const f = tplEntry.file || '';
        if (f.includes('/fonts/')) await addTemplate(f, 'font', 'fonts');
        else if (f.includes('/style/')) await addTemplate(f, 'css', 'undefined');
        else if (f.includes('/lua/')) await addTemplate(f, 'lua', 'luafilters');
      }
      for (const tplEntry of (manifest.templates.csl || [])) {
        await addTemplate(tplEntry.file, 'csl', 'csl');
      }
      for (const tplEntry of (manifest.templates.preparo || [])) {
        await addTemplate(tplEntry.file, 'preparo', 'preparo', tplEntry.documentType);
      }
    }

    // Fallback: scan template/ folders if manifest didn't list them
    if (tplResults.length === 0) {
      for (const [path, entry] of Object.entries(zip.files)) {
        if (entry.dir || !path.startsWith('template/')) continue;
        const filename = path.split('/').pop() || '';
        const ext = filename.toLowerCase().split('.').pop() || '';

        if (path.startsWith('template/indesign/') && ext === 'indd') {
          await addTemplate(path, 'indesign', 'indesign');
        } else if (path.startsWith('template/epub/fonts/')) {
          await addTemplate(path, 'font', 'fonts');
        } else if (path.startsWith('template/epub/style/') && ext === 'css') {
          await addTemplate(path, 'css', 'undefined');
        } else if (path.startsWith('template/epub/lua/') && ext === 'lua') {
          await addTemplate(path, 'lua', 'luafilters');
        } else if (path.startsWith('template/csl/') && ext === 'csl') {
          await addTemplate(path, 'csl', 'csl');
        } else if (path.startsWith('template/preparo/')) {
          await addTemplate(path, 'preparo', 'preparo');
        }
      }
    }

    // 6. Import config files as named templates (mapping.json, typography.json, etc.)
    const configMap: Record<string, string> = {
      'mapping': 'mapping.json',
      'typography': 'typography.json',
      'pagelayout': 'pagelayout.json',
      'grep': 'grep.json',
      'export': 'export.json'
    };

    for (const [key, templateName] of Object.entries(configMap)) {
      // Try manifest config path first, then fallback
      const configPath = manifest.config?.[key] || `build/indesign/config/${key}.json`;
      const entry = zip.file(configPath);
      if (!entry) continue;
      const content = await entry.async('text');
      tplResults.push({
        projectId: '',
        filename: templateName,
        templateType: 'config',
        templateSection: 'config',
        mimeType: 'application/json',
        content,
        size: content.length
      });
    }

    if (tplResults.length > 0) {
      dispatch('templatesImported', { templates: tplResults, count: tplResults.length });
    }

    conversionProgress = { stage: 'completed', progress: 100, current: totalItems, total: totalItems };
    console.log(`📦 ZIP import complete: ${textResults.length} docs, ${images.length} images, ${tplResults.length} templates`);
  }

  async function handleImport(): Promise<void> {
    if (selectedFiles.length === 0) return;

    error = null;
    isProcessing = true;

    // Group files by category
    const imageFiles = selectedFiles.filter(f => f.category === 'image');
    const templateFiles = selectedFiles.filter(f => f.category === 'template');
    const textFiles = selectedFiles.filter(f => f.category === 'text');
    const documentFiles = selectedFiles.filter(f => f.category === 'document');
    const zipFiles = selectedFiles.filter(f => f.category === 'zip');

    const total = selectedFiles.length;
    let processed = 0;
    const errors: string[] = [];

    try {
      // Process ZIP files (AutomaticBook project import)
      if (zipFiles.length > 0) {
        conversionProgress = { stage: 'Extracting ZIP...', progress: 0, current: 0, total: zipFiles.length };
        for (const { file } of zipFiles) {
          try {
            await processZipFile(file);
            processed++;
          } catch (err) {
            errors.push(`${file.name}: ${(err as Error).message}`);
          }
        }
        // ZIP handles its own dispatching — skip further processing if only ZIPs
        if (imageFiles.length === 0 && templateFiles.length === 0 && textFiles.length === 0 && documentFiles.length === 0) {
          selectedFiles = [];
          conversionProgress = { stage: 'completed', progress: 100, current: total, total };
          if (errors.length > 0) {
            error = `Import errors:\n${errors.join('\n')}`;
          }
          setTimeout(() => { isProcessing = false; }, 1000);
          return;
        }
      }

      // Process images
      if (imageFiles.length > 0) {
        conversionProgress = { stage: 'Processing images...', progress: 0, current: 0, total: imageFiles.length };
        const images: CreateImageInput[] = [];

        for (const { file } of imageFiles) {
          try {
            const dataUrl = await readFileAsDataUrl(file);
            const dimensions = await getImageDimensions(dataUrl);

            images.push({
              projectId: '', // Will be set by parent
              filename: file.name,
              mimeType: file.type,
              dataUrl,
              size: file.size,
              width: dimensions.width,
              height: dimensions.height
            });
            processed++;
            conversionProgress = { ...conversionProgress, current: images.length, progress: Math.round((processed / total) * 100) };
          } catch (err) {
            errors.push(`${file.name}: ${(err as Error).message}`);
          }
        }

        if (images.length > 0) {
          dispatch('imagesImported', { images, count: images.length });
        }
      }

      // Process templates
      if (templateFiles.length > 0) {
        conversionProgress = { stage: 'Processing templates...', progress: Math.round((processed / total) * 100), current: 0, total: templateFiles.length };
        const templates: CreateTemplateInput[] = [];

        for (const { file } of templateFiles) {
          try {
            const templateType = getTemplateType(file.name, file.type);
            console.log('📦 Processing template:', { filename: file.name, mimeType: file.type, templateType });
            let content: string;

            // Read text-based files as text, binary files as base64
            if (templateType === 'css' || templateType === 'lua' || templateType === 'csl' || file.type.startsWith('text/')) {
              content = await readFileAsText(file);
              console.log('📄 Read as text, content length:', content.length);
            } else {
              content = await readFileAsBase64(file);
              console.log('📄 Read as base64, content length:', content.length);
            }

            // Determine templateSection based on templateType
            let templateSection: TemplateSection = 'undefined';
            if (templateType === 'font') {
              templateSection = 'fonts';
            } else if (templateType === 'lua') {
              templateSection = 'luafilters';
            } else if (templateType === 'csl') {
              templateSection = 'csl';
            } else if (templateType === 'indesign') {
              templateSection = 'indesign';
            }
            console.log('📁 Template section:', templateSection);

            templates.push({
              projectId: '', // Will be set by parent
              filename: file.name,
              templateType,
              templateSection,
              mimeType: file.type || 'application/octet-stream',
              content,
              size: file.size
            });
            processed++;
            conversionProgress = { ...conversionProgress, current: templates.length, progress: Math.round((processed / total) * 100) };
          } catch (err) {
            console.error('❌ Error processing template:', file.name, err);
            errors.push(`${file.name}: ${(err as Error).message}`);
          }
        }

        console.log('📤 Dispatching templatesImported:', { count: templates.length, templates: templates.map(t => ({ filename: t.filename, type: t.templateType, section: t.templateSection })) });
        if (templates.length > 0) {
          dispatch('templatesImported', { templates, count: templates.length });
        }
      }

      // Process text files (direct import)
      const textResults: ConvertedFile[] = [];
      if (textFiles.length > 0) {
        conversionProgress = { stage: 'Importing text files...', progress: Math.round((processed / total) * 100), current: 0, total: textFiles.length };

        for (const { file } of textFiles) {
          try {
            const content = await readFileAsText(file);
            const metadata = extractDocumentMetadata(content);

            textResults.push({
              markdown: content,
              filename: formatDocumentName(file.name),
              originalFilename: file.name,
              metadata,
              size: file.size
            });
            processed++;
            conversionProgress = { ...conversionProgress, current: textResults.length, progress: Math.round((processed / total) * 100) };
          } catch (err) {
            errors.push(`${file.name}: ${(err as Error).message}`);
          }
        }
      }

      // Process documents (Pandoc conversion)
      const documentResults: ConvertedFile[] = [];
      if (documentFiles.length > 0) {
        conversionProgress = { stage: 'Converting documents...', progress: Math.round((processed / total) * 100), current: 0, total: documentFiles.length };

        for (let i = 0; i < documentFiles.length; i++) {
          const { file } = documentFiles[i];
          conversionProgress = {
            ...conversionProgress,
            current: i + 1,
            currentFile: file.name
          };

          try {
            const result = await convertDocumentToMarkdown(file, {
              ...options,
              onProgress: (progress) => {
                conversionProgress = {
                  ...conversionProgress,
                  stage: progress.stage,
                  progress: Math.round(((processed + (i + progress.progress / 100)) / total) * 100)
                };
              }
            });

            const metadata = extractDocumentMetadata(result.markdown);

            documentResults.push({
              markdown: result.markdown,
              filename: formatDocumentName(result.filename),
              originalFilename: result.filename,
              metadata,
              size: result.size
            });
            processed++;
          } catch (err) {
            errors.push(`${file.name}: ${(err as Error).message}`);
          }
        }
      }

      // Dispatch all document results (text + converted)
      const allDocuments = [...textResults, ...documentResults];
      if (allDocuments.length > 0) {
        dispatch('converted', {
          files: allDocuments,
          count: allDocuments.length
        });
      }

      // Show errors if any
      if (errors.length > 0) {
        error = `Import errors:\n${errors.join('\n')}`;
      }

      // Reset
      selectedFiles = [];
      conversionProgress = { stage: 'completed', progress: 100, current: total, total };

      setTimeout(() => {
        isProcessing = false;
      }, 1000);

    } catch (err) {
      error = `Import failed: ${(err as Error).message}`;
      isProcessing = false;
    }
  }

  function cancelSelection(): void {
    selectedFiles = [];
    error = null;
    warnings = [];
  }

  function getStageLabel(stage: string): string {
    const labels: Record<string, string> = {
      'starting': 'Starting...',
      'uploading': 'Uploading...',
      'converting': 'Converting with Pandoc...',
      'completed': 'Complete!',
      'error': 'Error'
    };
    return labels[stage] || stage;
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  function getTotalSize(): number {
    return selectedFiles.reduce((sum, f) => sum + f.file.size, 0);
  }

  // Count files by category
  $: fileCounts = {
    images: selectedFiles.filter(f => f.category === 'image').length,
    templates: selectedFiles.filter(f => f.category === 'template').length,
    documents: selectedFiles.filter(f => f.category === 'document' || f.category === 'text').length,
    zips: selectedFiles.filter(f => f.category === 'zip').length
  };

  // Check if we need Pandoc (for document conversion)
  $: needsPandoc = selectedFiles.some(f => f.category === 'document');
</script>

<div class="document-importer">
  <div class="importer-header">
    <h3>📥 Import files</h3>
    {#if needsPandoc}
      <div class="server-status" class:healthy={serverStatus?.status === 'healthy'} class:error={serverStatus?.status === 'error'}>
        {#if serverStatus?.status === 'healthy'}
          <span class="status-dot"></span>
          <span class="status-text">Pandoc ready</span>
        {:else if serverStatus?.status === 'error'}
          <span class="status-dot"></span>
          <span class="status-text">Server unavailable</span>
        {:else}
          <span class="status-text">Checking...</span>
        {/if}
      </div>
    {/if}
  </div>

  {#if needsPandoc && serverStatus?.status === 'error'}
    <div class="alert alert-warning">
      <strong>⚠️ Pandoc server not accessible</strong>
      <p>Document conversion (Word, ODT, etc.) requires the Pandoc server.</p>
      <p class="small">Images and templates can still be imported.</p>
      <button class="btn-small" on:click={checkServerStatus}>🔄 Retry</button>
    </div>
  {/if}

  <!-- Zone de drop -->
  <div
    class="drop-zone"
    class:dragging={isDragging}
    class:has-files={selectedFiles.length > 0}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
    role="button"
    tabindex="0"
  >
    <div class="drop-zone-content">
      <div class="drop-icon">📤</div>
      <p class="drop-title">Drop your files here</p>
      <p class="drop-subtitle">or</p>
      <label class="btn-primary">
        Choose files
        <input
          type="file"
          multiple
          on:change={handleFileInput}
          style="display: none;"
        />
      </label>
      <div class="drop-formats">
        <span class="format-group">📦 ZIP</span>
        <span class="format-group">🖼️ Images</span>
        <span class="format-group">📄 Documents</span>
        <span class="format-group">🎨 CSS</span>
        <span class="format-group">🔤 Fonts</span>
        <span class="format-group">📚 CSL</span>
      </div>
    </div>
  </div>

  <!-- Liste des fichiers sélectionnés -->
  {#if selectedFiles.length > 0}
    <div class="files-list">
      <div class="files-header">
        <div class="files-count-row">
          <span class="files-count">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}</span>
          {#if fileCounts.images > 0}
            <span class="category-badge images">🖼️ {fileCounts.images}</span>
          {/if}
          {#if fileCounts.templates > 0}
            <span class="category-badge templates">📎 {fileCounts.templates}</span>
          {/if}
          {#if fileCounts.documents > 0}
            <span class="category-badge documents">📄 {fileCounts.documents}</span>
          {/if}
          {#if fileCounts.zips > 0}
            <span class="category-badge zips">📦 {fileCounts.zips}</span>
          {/if}
        </div>
        <span class="files-size">{formatFileSize(getTotalSize())}</span>
      </div>
      <div class="files-items">
        {#each selectedFiles as item, index}
          <div class="file-item" class:image={item.category === 'image'} class:template={item.category === 'template'} class:zip={item.category === 'zip'}>
            <div class="file-icon">{item.icon}</div>
            <div class="file-info">
              <div class="file-name">{item.file.name}</div>
              <div class="file-meta">
                <span class="file-size">{formatFileSize(item.file.size)}</span>
                <span class="file-category">{item.category}</span>
              </div>
            </div>
            <button
              class="btn-icon-small"
              on:click={() => removeFile(index)}
              title="Remove from list"
              disabled={isProcessing}
            >
              ✕
            </button>
          </div>
        {/each}
      </div>
    </div>
  {/if}

    <!-- Warnings -->
    {#if warnings.length > 0}
      <div class="alert alert-warning">
        <strong>⚠️ Warnings:</strong>
        <ul class="warning-list">
          {#each warnings as warning}
            <li>{warning}</li>
          {/each}
        </ul>
      </div>
    {/if}

    <!-- Errors -->
    {#if error}
      <div class="alert alert-error">
        <strong>❌ Error</strong>
        <pre class="error-text">{error}</pre>
      </div>
    {/if}

  <!-- Barre de progression -->
  {#if isProcessing}
    <div class="progress-container">
      <div class="progress-header">
        <div class="progress-label">
          {getStageLabel(conversionProgress.stage)}
          {#if conversionProgress.currentFile}
            - {conversionProgress.currentFile}
          {/if}
        </div>
        <div class="progress-count">
          {conversionProgress.current}/{conversionProgress.total}
        </div>
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          style="width: {conversionProgress.progress}%"
        ></div>
      </div>
      <div class="progress-percent">{conversionProgress.progress}%</div>
    </div>
  {/if}

  <!-- Boutons d'action -->
  <div class="actions">
    {#if selectedFiles.length > 0 && !isProcessing}
      <button class="btn-secondary" on:click={cancelSelection}>
        Clear all
      </button>
      <button
        class="btn-primary"
        on:click={handleImport}
        disabled={needsPandoc && serverStatus?.status === 'error'}
      >
        📥 Import {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
      </button>
    {/if}
  </div>
</div>

<style>
  .document-importer {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background: var(--panel-background);
    border-radius: 8px;
    border: 1px solid var(--border-color);
  }

  .importer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .importer-header h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  /* Server Status */
  .server-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    background: var(--code-background);
    font-size: 0.85rem;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--text-muted);
  }

  .server-status.healthy .status-dot {
    background: var(--success-color, #10b981);
    animation: pulse 2s infinite;
  }

  .server-status.error .status-dot {
    background: var(--error-color, #ef4444);
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .status-text {
    color: var(--text-primary);
    font-weight: 500;
  }

  /* Drop Zone */
  .drop-zone {
    border: 2px dashed var(--border-color);
    border-radius: 8px;
    padding: 3rem 2rem;
    text-align: center;
    transition: all 0.3s;
    cursor: pointer;
    background: var(--code-background);
  }

  .drop-zone.dragging {
    border-color: var(--accent-color);
    background: var(--hover-background);
    transform: scale(1.02);
  }

  .drop-zone.has-files {
    padding: 1.5rem;
    border-style: solid;
    border-color: var(--accent-color);
  }

  .drop-zone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .drop-icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  .drop-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .drop-subtitle {
    font-size: 0.9rem;
    color: var(--text-muted);
    margin: 0;
  }

  .drop-formats {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
    font-size: 0.85rem;
    color: var(--text-muted);
    margin: 0;
  }

  .format-group {
    padding: 0.25rem 0.5rem;
    background: var(--hover-background);
    border-radius: 4px;
  }

  /* File Preview */
  .file-preview {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--hover-background);
    border-radius: 4px;
  }

  .file-icon {
    font-size: 2rem;
  }

  .file-info {
    flex: 1;
    text-align: left;
  }

  .file-name {
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .file-size {
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  .file-category {
    font-size: 0.7rem;
    text-transform: uppercase;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    background: var(--hover-background);
    color: var(--text-muted);
  }

  .file-item.image .file-category {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
  }

  .file-item.template .file-category {
    background: rgba(168, 85, 247, 0.15);
    color: #a855f7;
  }

  .file-item.zip .file-category {
    background: rgba(251, 146, 60, 0.15);
    color: #fb923c;
  }

  /* Files List */
  .files-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--code-background);
    border-radius: 8px;
    border: 1px solid var(--border-color);
  }

  .files-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--border-color);
  }

  .files-count-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .files-count {
    font-weight: 600;
    color: var(--text-primary);
  }

  .category-badge {
    font-size: 0.75rem;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    background: var(--hover-background);
  }

  .category-badge.images {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
  }

  .category-badge.templates {
    background: rgba(168, 85, 247, 0.15);
    color: #a855f7;
  }

  .category-badge.documents {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }

  .category-badge.zips {
    background: rgba(251, 146, 60, 0.15);
    color: #fb923c;
  }

  .files-size {
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  .files-items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: var(--panel-background);
    border-radius: 4px;
    transition: background 0.2s;
  }

  .file-item:hover {
    background: var(--hover-background);
  }

  .warning-list {
    margin: 0.5rem 0 0 1.5rem;
    padding: 0;
    font-size: 0.9rem;
  }

  .warning-list li {
    margin: 0.25rem 0;
  }

  .error-text {
    margin: 0.5rem 0 0 0;
    font-size: 0.85rem;
    white-space: pre-wrap;
    font-family: inherit;
  }

  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .progress-count {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--accent-color);
  }

  /* Conversion Options */
  .conversion-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    background: var(--code-background);
    border-radius: 4px;
  }

  /* Progress */
  .progress-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .progress-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-primary);
  }

  .progress-bar {
    height: 8px;
    background: var(--border-color);
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent-color);
    transition: width 0.3s ease;
  }

  .progress-percent {
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: right;
  }

  /* Alerts */
  .alert {
    padding: 1rem;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .alert-warning {
    background: rgba(251, 191, 36, 0.1);
    color: var(--warning-color, #fbbf24);
    border: 1px solid var(--warning-color, #fbbf24);
  }

  .alert-error {
    background: rgba(239, 68, 68, 0.1);
    color: var(--error-color, #ef4444);
    border: 1px solid var(--error-color, #ef4444);
  }

  .alert p {
    margin: 0.5rem 0 0 0;
  }

  .alert .small {
    font-size: 0.85rem;
    opacity: 0.9;
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
  }

  /* Buttons */
  .btn-primary, .btn-secondary, .btn-small {
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-size: 1rem;
  }

  .btn-primary {
    background: var(--accent-color);
    color: white;
  }

  .btn-primary:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }

  .btn-secondary:hover {
    background: var(--hover-background);
  }

  .btn-small {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }

  .btn-icon-small {
    background: transparent;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    color: var(--text-muted);
    transition: color 0.2s;
  }

  .btn-icon-small:hover {
    color: var(--error-color);
  }
</style>
